import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import LogoDark from '@images/logoDark.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import FormFieldComponent from '@/components/FormFieldComponent';
import { PasswordChallengeResponse } from '@brainvaulttech/dgr-react-sdk';
import { authService } from '@/services/authService';
import { useUserStore } from '@/stores/user';
import { initializeNetwork } from '@/services/Network';
import { useContext, useEffect, useState } from 'react';
import { removeToast, toast } from '@/components/ui/use-toast';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { EMAIL_ADDRESS_VALIDATION } from '@/utils/constant';
import { useFeatureStore } from '@/stores/featureStore';
import type { ExtendedUserResponseSchema } from '@/types/user.types';

const loginFormSchema = z.object({
  email: z.string({ message: 'Email address is required' }).trim(),
  password: z.string({ message: 'Password is required' }),
});

export const Login = () => {
  const navigate = useNavigate();
  const userStore = useUserStore((state) => state);
  const setFeatures = useFeatureStore((state) => state.setFeatures);
  const [sessionToastId, setSessionToastId] = useState<string | null>(null);
  const [emailBlurred, setEmailBlurred] = useState<boolean>(false);
  const { setIsDirty } = useContext(NavigationBlockerContext);
  const form = useForm<z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onChange',
  });
  useEffect(() => {
    if (userStore.user && userStore.accessToken && userStore.refreshToken) {
      navigate('/dashboard', { replace: true });
    }
  }, []);
  useEffect(() => {
    setIsDirty(false);
  }, []);

  const {
    formState: { isValid },
  } = form;
  const [error, setError] = useState<any>(null);
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');

  const onSubmitHandler = async () => {
    localStorage.removeItem('user');
    localStorage.removeItem('feature-store');

    setStatus('pending');
    setError(null);
    try {
      const data = await authService.login(form.getValues('email'), form.getValues('password'));
      setFeatures(data.features || []);
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        userStore.setResetPasswordToken(data.session);
        userStore.setUser(data.user as PasswordChallengeResponse & ExtendedUserResponseSchema);
        sessionToastId && removeToast(sessionToastId);
        setStatus('success');
        return navigate('/reset-password');
      } else {
        userStore.setAccessToken(data.tokens.accessToken);
        userStore.setRefreshToken(data.tokens.refreshToken);
        userStore.setUser(data.user as PasswordChallengeResponse & ExtendedUserResponseSchema);
        sessionToastId && removeToast(sessionToastId);
        setStatus('success');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1000);
      }
    } catch (err: any) {
      setStatus('error');
      setError(err);
    }
  };
  useEffect(() => {
    if (userStore.InactivityTimeout === true) {
      const id = toast({
        variant: 'destructive',
        title: 'Your session expired.',
        description: 'Please log in to continue.',
        className: '!relative !top-[-60px]',
        duration: 10000000,
      });
      setSessionToastId(id.id);
      userStore.setInactivityTimeout(false);
    }
  }, [userStore.InactivityTimeout]);

  return (
    <div className="bg-white flex flex-col items-center justify-center h-screen min-h-[600px]">
      <Card className="flex bg-white rounded- shadow w-[28%] min-w-[325px] max-w-[468px] max-h-[580px] p-10 gap-6">
        <form onSubmit={form.handleSubmit(onSubmitHandler)} className="w-full">
          <CardContent className="flex flex-col gap-6 w-full p-0">
            <div className="flex flex-col gap-6 justify-center items-center">
              <div>
                <img src={LogoDark} alt="logo" className="w-[135px] h-[70px]" />
              </div>
            </div>
            <FormProvider {...form}>
              <FormFieldComponent
                control={form.control}
                name="email"
                label="Email Address"
                placeholder="Enter Email Address"
                required
                autofocus
                {...EMAIL_ADDRESS_VALIDATION}
                onBlur={() => setEmailBlurred(true)}
                onChange={async (e) => {
                  if (emailBlurred) await form.trigger('email');
                  if (e.target.value.length === 0) setEmailBlurred(false);
                }}
              />
              <FormFieldComponent
                control={form.control}
                name="password"
                required
                label="Password"
                type="password"
                placeholder="Enter Password"
                maxLength={64}
              />
              {error?.response?.status === 500 ? (
                <div className="flex justify-start ">
                  <p className="text-error text-sm">Server down, please try after some time</p>
                </div>
              ) : error ? (
                <div className="flex justify-start ">
                  <p className="text-error text-sm">Email address or password is incorrect. Please try again.</p>
                </div>
              ) : null}
              <div className="flex flex-col justify-start gap-6">
                <span
                  onClick={() => {
                    sessionToastId && removeToast(sessionToastId);
                    navigate('/forgot-password');
                  }}
                  className="text-sm font-medium cursor-pointer hover:underline text-blue-500 underline w-fit"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </span>
                <Button
                  type="submit"
                  className="w-full text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm font-medium"
                  isLoading={status === 'pending'}
                  disabled={status === 'pending' || !isValid}
                  data-testid="login-button"
                >
                  Log In
                </Button>
              </div>
            </FormProvider>
          </CardContent>
        </form>
      </Card>

      <footer className=" text-center w-full gap-2 mt-4">
        <Link to="#" className="text-sm  hover:underline text-blue-500 underline">
          Terms & Conditions
        </Link>
        &nbsp; | &nbsp;
        <Link to="#" className="text-sm hover:underline text-blue-500 underline">
          Privacy Policy
        </Link>
        <p className="mt-2 text-center text-gray-500 text-xs">
          &copy; {new Date().getFullYear()} DGR Legal Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
};
